

// src/pages/About.js - Complete Figma-accurate version
import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/ui/Button';
import { homepageAPI } from '../services/api';
import image1 from '../assets/images/aboutus/image-1.jpg';
import image2 from '../assets/images/aboutus/image-2.jpg';
import image3 from '../assets/images/aboutus/image-3.jpg';
import image4 from '../assets/images/aboutus/image-4.jpg';
import image12 from'../assets/images/homepage/bottom.jpg';
import { Link } from 'react-router-dom';


const About = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback stats data
  const fallbackStats = [
    { number: '20+', label: 'years of combined expertise in bakery innovation', icon: '🎯' },
    { number: '500+', label: 'bakeries served across India with custom solutions', icon: '🏆' },
    { number: '23%', label: 'average reduction in production costs for clients', icon: '💰' },
    { number: '90%', label: 'client retention rate—built on trust and results', icon: '⭐' }
  ];

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await homepageAPI.getStats();
        if (data && data.length > 0) {
          setStats(data.map((stat, idx) => ({
            ...stat,
            icon: fallbackStats[idx]?.icon || '📊'
          })));
        } else {
          setStats(fallbackStats);
        }
        setLoading(false);
      } catch (err) {
        setStats(fallbackStats);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);
  return (
    <div className="min-h-screen bg-white lg:bg-cream">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-white lg:bg-cream">
        <div className="px-6 mx-auto max-w-7xl lg:px-8">
          <div className="flex flex-col items-center gap-12 lg:flex-row">
            <div className="flex-1">
              <img 
                src={image1} 
                alt="Transform Your Bakery"
                className="w-full h-[500px] object-cover rounded-3xl shadow-xl"
              />
            </div>
            <div className="flex-1 max-w-xl">
              <div className="mb-3 text-xs font-semibold tracking-wider uppercase text-primary">
                Our Services
              </div>
              <h1 className="mb-6 font-serif text-5xl font-bold leading-tight  text-text-primary">
                Transform Your Bakery Operations—Today!
              </h1>
              <p className="mb-8 text-lg leading-relaxed  text-text-primary">
                Enessa Foods is a dynamic leader in premium baking ingredients and cutting-edge bakery technologies. Driven by a passion for innovation, we serve both small and large food producers with expert solutions, including ultrasonic cutting systems and advanced bakery technologies tailored to unique production needs.

With deep industry experience and a global track record, Enessa Foods combines quality, reliability, and flexibility to meet the evolving demands of the baking sector. We prioritize collaboration, building lasting partnerships based on trust, performance, and mutual growth.

              </p>
              <div className="flex gap-4">
                <Button variant="primary"><a href="/products">Get Started</a></Button>
                <Button variant="secondary"><a href="/services">Learn More</a></Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* statistics Section */}
      <section className="py-16 bg-white lg:bg-cream">
        <div className="px-6 mx-auto max-w-7xl lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 font-serif text-4xl font-bold  text-text-primary">
              Numbers That Speak<br/>for Themselves
            </h2>
            <p className="text-brown-light">Backed by 24/7 technical support across all regions.
From product launches to partner growth—Enessa delivers.</p>
          </div>
          
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="p-8 text-center bg-white rounded-3xl shadow-soft animate-pulse">
                  <div className="mb-3 h-12 bg-gray-200 rounded"></div>
                  <div className="mb-3 h-12 bg-gray-200 rounded"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              stats.map((stat, idx) => (
                <div key={idx} className="p-8 text-center bg-white rounded-3xl shadow-soft">
                  <div className="mb-3 text-4xl">{stat.icon}</div>
                  <div className="mb-3 text-5xl font-bold text-primary">{stat.number}</div>
                  <p className="text-sm leading-relaxed  text-text-primary">{stat.label}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Service Sections */}
      {[
        {
          title: 'Empower Your Bakery To Rise Higher—With Purpose!',
          subtitle: 'Custom Product Development',
          description: 'Enessa Foods empowers bakers with high-quality, innovative baking ingredients that boost performance, consistency & product excellence. We offer cost-effective, sustainable solutions tailored to the needs of both artisan bakeries & large-scale operations. With strict quality control & a focus on customer satisfaction, we help bakeries succeed in a competitive market.',
          features: [
            'Premium, performance-driven baking ingredients',
            'Reliable solutions for artisan and industrial bakeries',
            'Commitment to sustainability and innovation',
            'strict quality control for consistent result'
          ],
          image: image2,
          reverse: false
        },
        {
          title: 'Bake A Better Future—Start with Enessa\'s Vision!',
          subtitle: 'Recipe Optimization & Cost Reduction',
          description: 'Our Vision at Enessa Foods is to lead the baking industry as a dynamic, innovative provider of premium, sustainable baking ingredients. We aim to drive positive change by promoting eco-friendly practices, supporting bakers worldwide, and delivering exceptional quality and customer care. Our goal is to help bakers across India and beyond create delicious, high-quality baked goods that inspire and delight.',
          features: [
            'Leadership in premium, sustainable baking ingredients',
            'Focus on innovation and quality',
            'Strong commitment to customer relationships',
            'Global support for bakers, from India to the world'
          ],
          image: image3,
          reverse: true
        }
      ].map((service, idx) => (
        <section key={idx} className={`py-16 ${idx % 2 === 0 ? ' bg-white lg:bg-cream' : 'bg-white lg:bg-cream'}`}>
          <div className="px-6 mx-auto max-w-7xl lg:px-8">
            <div className={`flex flex-col ${service.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}>
              <div className="flex-1">
                <div className="mb-2 text-xs font-semibold tracking-wider uppercase text-text-primary">
                  {service.subtitle}
                </div>
                <h3 className="mb-5 font-serif text-4xl font-bold  text-text-primary">{service.title}</h3>
                <p className="mb-6 leading-relaxed  text-text-primary">{service.description}</p>
                
                <ul className="mb-6 space-y-3">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <span className=" text-primary">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="flex gap-4">
                  <Button variant="primary"><a href="/products">Get Started</a></Button>
                  <Button variant="secondary"><a href="/services">Learn More</a></Button>
                </div>
              </div>
              <div className="flex-1">
                <img src={service.image} alt={service.title} className="object-cover w-full shadow-lg h-96 rounded-3xl" />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* More Services Grid */}
      <section className="py-16 bg-white lg:bg-cream">
        <div className="px-6 mx-auto max-w-7xl lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 font-serif text-4xl font-bold  text-text-primary">
              Choose Excellence.<br/>Choose Enessa.
            </h2>
            <p className="max-w-3xl mx-auto  text-text-primary">
              When you partner with Enessa Foods, you gain a dedicated ally invested in your success. Why choose us? Here are a few reasons:
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: '🔬',
                title: 'Uncompromising Quality',
                
                description: 'We use the finest raw materials and advanced formulations to enhance the quality, consistency and characteristics of your products. Each ingredient is tested to ensure it performs reliably in your recipes.'
              },
              {
                icon: '🏪',
                title: 'Process Optimization',
                
                description: 'Our improvers and enzymes improve dough handling and production efficiency, resulting in smoother operations. While, you will minimize wastage and reduce losses with our solutions guiding your process.'
              },
              {
                icon: '🍰',
                title: 'Cost Savings',
            
                description: 'Enessa Foods helps bring down your raw material costs with competitive pricing and innovative ingredients that let you use less without sacrificing quality. Greater yield & longer shelf life mean better profitability.'
              },
              {
                icon: '📊',
                title: 'Sustainable Practices',
                
                description: 'We are committed to eco friendly practices, from using sustainable ingredient sources to offering clean-label products that remove harmful additives. By reducing our environmental footprint and yours, we contribute to a greener future.'
              },
              {
                icon: '📈',
                title: 'Exceptional Support',
                
                description: 'Our relationship doesn’t end at sales. We pride ourselves on providing excellent customer service and technical support. Need advice on a recipe or process? Our food technologists and bakery consultants are just a call away to assist you.'
              }
            ].map((item, idx) => (
              <div key={idx} className="p-8 bg-white lg:bg-cream rounded-3xl">
                <div className="mb-4 text-5xl">{item.icon}</div>
                <div className="mb-2 text-xs font-semibold uppercase text-primary">{item.subtitle}</div>
                <h4 className="mb-3 text-xl font-bold  text-text-primary">{item.title}</h4>
                <p className="text-sm leading-relaxed  text-text-primary">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20" style={{background: 'bg-white lg:bg-cream'}}>
        <div className="px-6 mx-auto max-w-7xl lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 font-serif text-4xl font-bold  text-text-primary">
              Partner With Progress—Grow With Enessa.
            </h2>
            <p className="max-w-3xl mx-auto text-lg  text-text-primary">
              Enessa Foods works with brand-name partners across India as well as innovative pioneers in the food industry. Through this robust partner network, we ensure customers get the best possible service with maximum quality. When you collaborate with us, you become part of a network dedicated to excellence.
            </p>
          </div>

          <div className="grid gap-8 mb-12 md:grid-cols-2">
            {[
              {
                icon: '🎯',
                title: 'Access New Markets',
                description: 'Leverage first-class combinations of new and proven solutions to tap into new market segments ahead of the competition.'
              },
              {
                icon: '🔄',
                title: 'Enhanced Reach',
                description: 'Utilize more efficient marketing and sales channels backed by a strong partner (Enessa Foods) focused on the baking sector. We help amplify your market presence.'
              },
              {
                icon: '💡',
                title: 'Integration of Technologies',
                description: 'We help integrate partner products and cutting-edge technologies into your operations to meet your requirements and strengthen your competitiveness.'
              },
              {
                icon: '⚡',
                title: 'Cost Efficiency',
                description: 'Achieve cost savings through optimized solutions and shared resources within our partner network.'
              }
            ].map((item, idx) => (
              <div key={idx} className="p-8  text-text-primary bg-white/10 backdrop-blur-sm rounded-3xl">
                <div className="mb-4 text-5xl">{item.icon}</div>
                <h4 className="mb-3 text-2xl font-bold">{item.title}</h4>
                <p className="leading-relaxed  text-text-primary">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a href="/contact" className="inline-block px-10 py-4 text-lg font-medium transition-all bg-white rounded-full shadow-xl text-primary hover:bg-white/90">
              Let's Partner Up
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
     {/* Final CTA with Chef */}
      <section className="py-24 lg:py-40 bg-white lg:bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-[40px] p-12 lg:p-24 shadow-soft flex flex-col lg:flex-row-reverse items-center gap-16">
            <div className="flex-1">
              <img src={image12} alt="Chef" className="w-full rounded-3xl shadow-2xl" />
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h2 className="font-heading text-heading-lg text-text-primary mb-8 leading-tight">
                Let’s Rise Together—Start Today!
              </h2>
              <p className="font-body text-body-lg text-text-primary mb-10 leading-normal">
                Ready to elevate your bakery business? Partner with Enessa Foods for innovative solutions, reliable performance and zero-risk growth.
              </p>
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                <Link to="/contact"><Button variant="primary" size="large">Contact Us</Button></Link>
                <Link to="/contact#inquiry-section"><Button variant="secondary" size="large">Enquiry Form</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};


export default About;


